/**
 * DEFUSE S15 — typed property descriptors for the container / reference kinds
 * (Block insert / Rectangular Associative Array / Hatch / Viewport). Special
 * fields only. Wipeout has no special fields (common object props only).
 *
 * Block BLOCK_RECORD / BlockDefinition metadata (Description / Block Unit /
 * Allow Exploding / …) is behavior-backed: its typed home is a graph node, not
 * the INSERT entity. The descriptor projects the AutoCAD DEFAULT the production
 * preview shows for an insert with no special record (Block Unit "0", Allow-*
 * "Yes", …); S16 resolves the real value from the BlockDefinition graph accessor
 * (P2 #12). `Hyperlink` is the ONE writable per-insert metadata row (P2 #6).
 */

import type {
  ArrayRectEntity,
  BlockInsertEntity,
  HatchEntity,
  ViewportEntity,
  WipeoutEntity,
} from "@dxf-ai-editor/drawing-model";

import { formatNumber } from "../geometryEntityTypes";
import { hatchStyleLabel, patternTypeLabel } from "../previewBuilders";
import { parseViewportScaleFraction } from "../viewportMutateEntity";
import {
  calc,
  coord,
  deferred,
  make,
  num,
  txt,
  type EditorKind,
  type TypedPropertyDescriptor,
} from "./types";

const XY_PRESERVE = { zPreserve: true, docPrecisionReconcile: true } as const;

const HATCH_STYLE_CODE: Record<string, number> = { Normal: 0, Outer: 1, Ignore: 2 };
const PATTERN_TYPE_CODE: Record<string, number> = {
  "User defined": 0,
  Predefined: 1,
  Custom: 2,
};

/**
 * N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (@codex P2 round 4) — decode a
 * "Pattern type" CELL/CHANGE value EXACTLY like the legacy translator's own
 * Hatch case block does (apps/web `propertyTranslator.ts`: "DXF group 76 —
 * label or raw int" — `patternTypeByLabel[value.trim()] ?? (Number.isInteger
 * (Number(value)) ? Number(value) : undefined)`). The Double `readOnly`
 * companions below (`readOnlyFromBag` / `readOnlyFromChanges`) MUST accept
 * the identical two-form vocabulary the REAL Pattern-type write path
 * accepts — a caller (AI `PROPERTY`, a CLI CHPROP-style dispatch, MATCHPROP)
 * can legitimately submit the raw DXF integer ("0"/"1"/"2"), not only the
 * decoded label ("User defined"/"Predefined"/"Custom"); treating a numeric
 * "0" as "unrecognised" would wrongly gate a genuinely User-defined
 * transition. `PATTERN_TYPE_CODE` (this file, the label half) stays the ONE
 * TRUTH for the label form; this only ADDS the same integer fallback
 * `propertyTranslator.ts` already has, so the two never diverge on which
 * inputs they accept.
 */
function decodePatternTypeValue(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed in PATTERN_TYPE_CODE) return PATTERN_TYPE_CODE[trimmed];
  const n = Number(trimmed);
  return Number.isInteger(n) ? n : undefined;
}

/** Viewport paper-to-model scale ratio (height / view height), mirroring
 *  dxfGeometry.buildViewportPreview (0.01 fallback for a non-positive view). */
function viewportScaleRatio(e: ViewportEntity): number {
  return e.viewHeight && e.viewHeight > 0 ? e.height / e.viewHeight : 0.01;
}

/** Format a viewport scale ratio into the `1:100` / `2:1` palette label —
 *  mirrors dxfGeometry.formatViewportScale (the ONE producer of the "Scale"
 *  row). S15/S16 canonical home; the apps/web copy migrates to import it. */
function formatViewportScale(scale: number): string {
  if (scale <= 0) return "1:100";
  if (scale >= 1) {
    const ratio = Math.round(scale * 100) / 100;
    const isWhole = Math.abs(ratio - Math.round(ratio)) < 0.01;
    return isWhole ? `${Math.round(ratio)}:1` : `${ratio.toFixed(2)}:1`;
  }
  const inverse = 1 / scale;
  const wholeInverse = Math.round(inverse);
  if (Math.abs(inverse - wholeInverse) < 0.5) return `1:${wholeInverse}`;
  return `1:${Math.round(inverse * 10) / 10}`;
}

/** parse a `num:den` viewport scale label back to the ratio (S15 the descriptor
 *  `read`↔`toRaw` no-op comparison value). Delegates to the ONE-TRUTH
 *  `parseViewportScaleFraction` (shared with the `viewportScaleMutator`, which needs
 *  BOTH rational parts) so the accept/reject grammar is defined once. Rejects
 *  (→ null) an unparseable / zero-denominator label so a bad edit no-ops. */
function parseViewportScaleLabel(s: string): number | null {
  const fraction = parseViewportScaleFraction(s);
  return fraction ? fraction.num / fraction.denom : null;
}

/** viewport MODEL-space view field — routes through applyViewportSetView (view
 *  + scale sync), so it carries sideEffects="viewportMutator" (S15 must use the
 *  mutator, not a plain field set). */
function viewportView(
  label: string,
  path: string,
  read: (e: ViewportEntity) => unknown,
): TypedPropertyDescriptor {
  return make(label, path.includes(".") ? "point-coord" : "number", {
    path,
    read: (e) => read(e as ViewportEntity),
  }, {
    constraints: { numeric: { precision: 3 }, sideEffects: "viewportMutator" },
  });
}

/** read-only block-record-sourced metadata row projecting the AutoCAD default;
 *  S16 overlays the graph-resolved value. */
function blockMeta(
  label: string,
  defaultValue: string,
  editorKind: EditorKind = "text",
): TypedPropertyDescriptor {
  return make(label, editorKind, undefined, {
    channel: "read-only-calc",
    constraints: { readOnly: true },
    display: () => defaultValue,
  });
}

export const blockDescriptors: TypedPropertyDescriptor[] = [
  txt<BlockInsertEntity>("Name", "blockName", (e) => e.blockName),
  coord<BlockInsertEntity>("Insertion X", "insertionPoint.x", (e) => e.insertionPoint.x, XY_PRESERVE),
  coord<BlockInsertEntity>("Insertion Y", "insertionPoint.y", (e) => e.insertionPoint.y, XY_PRESERVE),
  num<BlockInsertEntity>("Scale X", "scaleX", (e) => e.scaleX),
  num<BlockInsertEntity>("Scale Y", "scaleY", (e) => e.scaleY),
  num<BlockInsertEntity>("Scale Z", "scaleZ", (e) => e.scaleZ),
  num<BlockInsertEntity>("Rotation", "rotation", (e) => e.rotation),
  // MINSERT counts — the Block translator clamps `Math.max(1, Math.round(...))`,
  // so min:1 + int mirror the positive-count invariant (P2 R4 #5).
  num<BlockInsertEntity>("Column count", "columnCount", (e) => e.columnCount, { min: 1, int: true }),
  num<BlockInsertEntity>("Row count", "rowCount", (e) => e.rowCount, { min: 1, int: true }),
  num<BlockInsertEntity>("Column spacing", "columnSpacing", (e) => e.columnSpacing),
  num<BlockInsertEntity>("Row spacing", "rowSpacing", (e) => e.rowSpacing),
  // Hyperlink — WRITABLE per-insert metadata (blockInsertPatchableProperties)
  // but with NO plain INSERT typed field (empty path), so the write is
  // side-effect-routed through the block-hyperlink translator/accessor, NOT a
  // direct `typedFieldRef.path` set (P2 R6 #5 — same empty-path + sideEffects
  // shape as viewport Scale). read/display "" until S16 wires the accessor.
  make("Hyperlink", "text", { path: "", read: () => "" }, {
    constraints: { sideEffects: "blockHyperlinkMutator" },
    display: () => "",
    toRaw: (s) => s,
  }),
  blockMeta("Description", ""),
  blockMeta("Block Unit", "0"),
  blockMeta("Block Unit Factor", "1", "number"),
  blockMeta("Allow Exploding", "Yes", "boolean"),
  blockMeta("Allow Non-Uniform Scale", "Yes", "boolean"),
];

/**
 * S-ARRAYRECT-AXISANGLE-UNIT (Codex P2, local review rounds 4 + 9 on the
 * render-arm slice) — "Axis angle" is degree-projected AND
 * neutral-baseline-corrected: AutoCAD's Properties palette always
 * shows/accepts an array's Axis angle in DEGREES, matching every other angle
 * field in the app (Rotation, Start/End angle, …), with 0° meaning "not
 * rotated from the array's authored/standard orientation" — matching every
 * other rotation field's zero convention.
 *
 * The typed `axisAngle` STORAGE is NOT that user-facing rotation directly:
 * for an ASSOCIATIVE array it is the resolved
 * `AcDbAssocArrayRectangularParameters` "AxesAngle" named-value-param
 * (RADIANS, byte-matched against real AutoCAD files —
 * packages/dxf-io/src/handleResolution.ts RECT_NAMED_VALUE_PARAMS /
 * packages/dxf-io/src/assocArrayParser.ts's "Researched deferrals" section)
 * — which is the ANGLE BETWEEN THE ROW AND COLUMN AXES (ODA
 * `CalculateDefaultMatrix`: `Y-axis = X-axis-direction.rotateBy(AxesAngle)`),
 * NOT the array's absolute orientation. A STANDARD orthogonal array (rows
 * perpendicular to columns — the overwhelming common case) therefore
 * persists AxesAngle = π/2 (90°), confirmed byte-identical against two real
 * AutoCAD fixtures (packages/drawing-model/src/dxfBridge.arrayAssoc.test.ts:
 * "AxesAngle in radians (π/2 = the 90° default)"). Round 4 correctly solved
 * the RADIANS-vs-DEGREES unit ambiguity but (round 9, Codex P2) still
 * projected the raw π/2 verbatim as "90°", so a STANDARD imported array
 * displayed/rendered/gripped as if rotated 90° from neutral — Codex: "the
 * render path should treat π/2/90° as the neutral axes angle".
 *
 * `resolveArrayAxisAngleDisplayDeg`/`arrayAxisAngleDisplayDegToRaw` apply
 * the ADDITIONAL -90°/+90° neutral-baseline correction for the associative
 * regime on top of the round-4 unit conversion, so the DISPLAYED/rendered
 * value is 0° for the standard orthogonal case (matching AutoCAD's own
 * "not rotated" convention) instead of 90°. The array's TRUE absolute
 * orientation (`xAxisDirection`, AutoCAD's "RowProfile" geom param) is a
 * SEPARATE, still-undecoded "researched deferral" field
 * (assocArrayParser.ts: "No real-world fixture with a non-default axis is
 * available to verify the edge-param curve encoding") — this correction is
 * the best-available approximation given that gap: correct for the standard
 * (non-sheared, non-rotated) array, which is what every currently-decodable
 * real fixture represents; a genuinely SHEARED (non-90°-between-axes) OR
 * whole-array-rotated import is a documented residual gap tracked against
 * the SAME xAxisDirection deferral, not newly introduced here.
 *
 * `display`/`toRaw` on the descriptor below share these SAME exported
 * functions with `arrayAssociativeMutator` (sideEffectMutators.ts) — one
 * truth, so the write boundary (round 4/5), the neutral-baseline correction
 * (round 9) and the Associative-toggle unit conversion (round 5) can never
 * drift out of sync. `display` projects storage→corrected-degrees for the
 * panel — mirrored 1:1 by apps/web `dxfGeometry.ts` `buildArrayRectPreview`
 * (the ONE OTHER producer of this label; the S16 `panelReadCutover` census
 * gate keeps the two byte-identical). `toRaw` is the exact inverse, so a
 * no-op re-type of the CURRENTLY DISPLAYED value round-trips exactly.
 */
export function resolveArrayAxisAngleDisplayDeg(
  axisAngle: number | undefined,
  associative: boolean,
): number {
  const raw = axisAngle ?? 0;
  // Round 4 (unit): radians -> degrees for an associative array. Round 9
  // (neutral baseline): AxesAngle's OWN neutral/standard value is π/2 (90°),
  // not 0 — subtract it so the displayed/rendered rotation is 0° for a
  // standard (non-sheared) array, matching every other rotation field's
  // zero convention.
  return associative ? (raw * 180) / Math.PI - 90 : raw;
}

export function arrayAxisAngleDisplayDegToRaw(
  displayDeg: number,
  associative: boolean,
): number {
  // Exact inverse of resolveArrayAxisAngleDisplayDeg: add back the 90°
  // neutral-baseline offset (round 9), then degrees -> radians (round 4).
  return associative ? ((displayDeg + 90) * Math.PI) / 180 : displayDeg;
}

function axisAngleDescriptor(): TypedPropertyDescriptor {
  return make(
    "Axis angle",
    "number",
    { path: "axisAngle", read: (e) => (e as ArrayRectEntity).axisAngle },
    {
      constraints: { numeric: { precision: 3 } },
      display: (e) => {
        const entity = e as ArrayRectEntity;
        return formatNumber(
          resolveArrayAxisAngleDisplayDeg(entity.axisAngle, entity.associative),
        );
      },
      toRaw: (s, entity) => {
        const deg = Number(s);
        if (!Number.isFinite(deg)) return null;
        const assoc = (entity as ArrayRectEntity | undefined)?.associative ?? false;
        return arrayAxisAngleDisplayDegToRaw(deg, assoc);
      },
    },
  );
}

export const arrayRectDescriptors: TypedPropertyDescriptor[] = [
  // z.number().int().min(1) — the typed writer ROUNDS a fractional count
  // (legacy Math.round parity) but REJECTS a value below 1 (min:1 + int
  // descriptor bound → the whole edit no-ops). This is the documented S15
  // Slice A INTENDED DIVERGENCE: the deleted legacy ArrayRect branch silently
  // CLAMPED out-of-range counts to 1 — current semantics never fabricate a
  // count the user did not type (pinned in extGeomSliceA.parity.test.ts).
  // min:1 + int also drive the S16 widget bounds (P2 #7). W2.1
  // (S16-ARRAYRECT-READONLY-ALIGN) — Rows/Columns/Row spacing/Column spacing
  // stay UNCONDITIONALLY editable (associative OR not): AW-1 (#535,
  // `packages/drawing-model/src/dxfBridge.arrayAssocExport.ts`
  // `applyAssocArrayWriteBack`, called unconditionally from the main export
  // path `dxfBridge.export.ts`) already surgically patches every dirty field
  // of this exact 8-field matrix (columnCount/rowCount/columnSpacing/
  // rowSpacing/levelCount/levelSpacing/axisAngle/rowElevation) into the
  // preserved graph bytes on export — a Properties/CHPROP/MATCHPROP/agent
  // edit here DOES survive a save/reload today. Only `Associative` itself
  // stays read-only (see below) — AutoCAD's own Properties palette lets you
  // resize an associative array's grid, it just never lets you flip its
  // associativity there.
  num<ArrayRectEntity>("Rows", "rowCount", (e) => e.rowCount, { min: 1, int: true }),
  num<ArrayRectEntity>("Columns", "columnCount", (e) => e.columnCount, { min: 1, int: true }),
  num<ArrayRectEntity>("Row spacing", "rowSpacing", (e) => e.rowSpacing),
  num<ArrayRectEntity>("Column spacing", "columnSpacing", (e) => e.columnSpacing),
  coord<ArrayRectEntity>("Base X", "basePoint.x", (e) => e.basePoint.x, XY_PRESERVE),
  coord<ArrayRectEntity>("Base Y", "basePoint.y", (e) => e.basePoint.y, XY_PRESERVE),
  // S-ARRAYRECT-BASEPOINT-Z (W1.5) — the array's base elevation
  // (`ArrayRectEntity.basePoint.z`, DXF group 30). NOT in
  // `editableProperties` (dxfGeometry.ts) — full Z editing is out of this
  // render-arm's scope, same deferred class as the Level count/spacing
  // channels below. `calc` (no custom `display`) makes this the SAME
  // `isSingleSelectCompactNumericResidual` bag-sourced class as LINE
  // "Start Z"/"End Z" and POINT "Position Z" — the Float32 compact store
  // does not losslessly round-trip an elevation, so the panel keeps
  // reading the full-precision bag string, exactly like those two fields.
  // Without this descriptor "Base Z" was a production preview key with NO
  // typed-model home at all — an S16 panelReadCutover census gap the
  // original W1.5 commit left unclassified (found while adding coverage
  // for the unrelated S-ARRAYRECT-AXISANGLE-UNIT round-4 fix).
  calc<ArrayRectEntity>("Base Z", (e) => e.basePoint?.z ?? 0),
  axisAngleDescriptor(),
  // display Yes/No (default boolean projection == buildArrayRectPreview); the
  // WRITE accepts exactly the legacy translator's truthy-token set (yes/true/1
  // — the deleted ArrayRect branch parse; S15 Slice A write parity — NOT the
  // wider defaultParse set, which also took "on"). SIDE-EFFECT-routed
  // (`arrayAssociativeMutator`, sideEffectMutators.ts) — NOT a plain field
  // set (Codex P2, local review round 5): toggling `Associative` changes
  // `axisAngle`'s STORAGE UNIT (see `axisAngleDescriptor` above), so a plain
  // write would silently reinterpret the stored angle in the new unit
  // without converting it (an imported 90° associative array toggled to
  // non-associative would render "1.5708°" instead of "90°"). The mutator
  // converts `axisAngle` to the semantically-equivalent value in the new
  // unit — the real-world angle survives the toggle unchanged.
  // S16-ARRAYRECT-READONLY-ALIGN (W2.1) — ALWAYS read-only, independent of
  // the CURRENT value and independent of AW-1's write-back (#535, already
  // landed — see the Rows/Columns/spacing comment above): AutoCAD does not
  // let you flip an existing array's associativity from the Properties
  // palette — the only way is EXPLODE, which destroys the associative array
  // object entirely (not a toggle on it). `readOnly: true` short-circuits
  // `applyTypedPropertyEditWithDescriptor` BEFORE it ever reaches the
  // `sideEffects` dispatch below, so the mutator wiring stays intact but
  // inert — a future dedicated re-associate command is a one-line deletion
  // of this flag, zero call-site churn.
  make(
    "Associative",
    "boolean",
    { path: "associative", read: (e) => (e as ArrayRectEntity).associative },
    {
      constraints: { sideEffects: "arrayAssociativeMutator", readOnly: true },
      toRaw: (s) => ["yes", "true", "1"].includes(s.trim().toLowerCase()),
    },
  ),
];

export const hatchDescriptors: TypedPropertyDescriptor[] = [
  // Pattern change is NOT a plain field write — it resets solidFill / gradient /
  // customPatternLines, so S15 routes it through the hatch pattern mutator
  // (P2 #14).
  make("Pattern", "text", { path: "patternName", read: (e) => (e as HatchEntity).patternName }, {
    constraints: { sideEffects: "hatchPatternMutator" },
  }),
  // HATCH Scale is positive-finite (translator rejects 0/negative) — P2 #15.
  make("Scale", "number", { path: "patternScale", read: (e) => (e as HatchEntity).patternScale }, {
    constraints: { numeric: { precision: 3, exclusiveMin: 0 }, docPrecisionReconcile: true },
  }),
  make("Angle", "number", { path: "patternAngle", read: (e) => (e as HatchEntity).patternAngle }, {
    constraints: { numeric: { precision: 3 }, docPrecisionReconcile: true },
  }),
  // Solid is a DERIVED READ-ONLY display of the solid-fill state, NOT an
  // independently-editable toggle (@codex P2 ×3 on 095cb925 — the Solid cluster).
  // AutoCAD/ezdxf model solid fill as the "SOLID" PATTERN (you pick the pattern,
  // you don't toggle a Solid boolean); the DELETED legacy HATCH branch had NO
  // `case "Solid"` — `solidFill` (DXF 70) was ONLY derived inside `case "Pattern"`
  // (`= name === "SOLID"`); and the scene adapter ORs `patternName === "SOLID"`
  // with the flag (hatchToRenderable), so a "Solid=No but patternName=SOLID" state
  // would render solid — an independent Solid=No is meaningless. So Solid is
  // read-only-calc: the panel SHOWS the derived Yes/No (mirroring the import
  // factory's `Solid` row), Pattern="SOLID" via the panel STILL sets solidFill via
  // the mutator's Pattern⇒derive path, and there is no independent Solid write to
  // order / parse / couple. Not owned (excluded from the side-effect writable set).
  make("Solid", "boolean", { path: "solidFill", read: (e) => (e as HatchEntity).solidFill }, {
    channel: "read-only-calc",
    constraints: { readOnly: true },
    display: (e) => ((e as HatchEntity).solidFill ? "Yes" : "No"),
  }),
  num<HatchEntity>("Elevation", "elevation", (e) => e.elevation),
  // display Yes/No (default boolean projection); the WRITE accepts exactly the
  // deleted legacy HATCH Associative case's truthy-token set (yes/true/1 — NOT
  // the wider `bool`/defaultParse set, which also takes "on"), matching the
  // sibling ArrayRect `Associative` (S15 Slice E write parity, @codex P3).
  make(
    "Associative",
    "boolean",
    { path: "associative", read: (e) => (e as HatchEntity).associative },
    { toRaw: (s) => ["yes", "true", "1"].includes(s.trim().toLowerCase()) },
  ),
  // enum rows render LABELS not raw codes (P2 #10); read=raw code, toRaw inverts
  // and REJECTS an unknown label (→ undefined = no-op) like the translator's
  // `code !== undefined` guard (P2 R4 write-robustness).
  make("Hatch style", "enum", { path: "hatchStyle", read: (e) => (e as HatchEntity).hatchStyle }, {
    display: (e) => hatchStyleLabel((e as HatchEntity).hatchStyle),
    toRaw: (s) => (s in HATCH_STYLE_CODE ? HATCH_STYLE_CODE[s] : undefined),
  }),
  make("Pattern type", "enum", { path: "patternType", read: (e) => (e as HatchEntity).patternType }, {
    display: (e) => patternTypeLabel((e as HatchEntity).patternType),
    toRaw: (s) => (s in PATTERN_TYPE_CODE ? PATTERN_TYPE_CODE[s] : undefined),
  }),
  // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR — "Double" (DXF group 77, `doubleLine`)
  // draws a second line family at 90° to the first, for a CROSSHATCH effect.
  // Autodesk's Hatch and Gradient dialog (Hatch tab) is explicit: "For
  // user-defined patterns, draws a second set of lines at 90 degrees to the
  // original lines, creating a crosshatch" and "This option is available only
  // when Type is set to User Defined" — i.e. `patternType === 0` (User
  // defined). ezdxf's own group-77 doc ("hatch pattern double flag (pattern
  // fill only)") + this entity's own DXF-bridge comment
  // (dxfBridge.entityConversion.ts: "doubleLine is emitted only for
  // definition lines … 78 + 53/43/44/45/46/79/49") agree: the flag only means
  // anything alongside a pattern-definition-line block, which only a
  // user-defined (or custom) pattern carries — a Predefined (.pat-referenced)
  // or Solid ("SOLID" is a Predefined pattern name) hatch never has one.
  // AutoCAD does not HIDE the row for a non-user-defined hatch — the sibling
  // Scale/Spacing pair in the SAME dialog is the documented precedent ("For
  // user-defined hatches … the Scale slot in Properties is greyed out and the
  // Spacing slot will be the operable one"): the row stays VISIBLE, only the
  // EDIT is disabled. So this is a per-INSTANCE (entity-state) `readOnly`
  // predicate, not a static flag or a bag-membership gate — the function form
  // `resolveReadOnly` (descriptors/types.ts) was reserved for exactly this
  // case and is exercised here for the first time by a real registry entry.
  // Display/write mirror the sibling boolean `Associative` immediately above
  // (default Yes/No projection; toRaw accepts the same yes/true/1 truthy set,
  // NOT the wider "on" vocabulary) — one boolean convention for every Hatch
  // checkbox-style field.
  //
  // @codex P2 — the entity-STATE `readOnly` above has no live entity at the
  // SHELL layer (`typedExtendedGeometryShellRejects` operates on the STRING
  // preview bag), so a Predefined/Custom-pattern hatch's "Double" write would
  // otherwise sail into the bag/audit/MATCHPROP-copy-source while the
  // document writer silently rejects it — shell↔document divergence.
  // `readOnlyFromBag` re-derives the identical verdict from the bag's own
  // already-decoded "Pattern type" sibling row (via the SAME `PATTERN_TYPE_
  // CODE` inverse the "Pattern type" descriptor's own `toRaw` uses — one
  // truth, not a hand-duplicated rule) instead of the typed entity.
  //
  // @codex P2 round 3 — a SINGLE apply that changes BOTH "Pattern type" AND
  // "Double" together must not evaluate "Double" against the STALE
  // pre-apply patternType: "Pattern type" is an enum, excluded from typed-
  // writer ownership, so it lands via the SEPARATE legacy-translator step
  // that runs AFTER this label's own accumulator loop
  // (`buildTypedFieldPatch`) — the loop never sees the sibling update.
  // `readOnlyFromChanges` peeks the SAME `changes` batch for a "Pattern
  // type" entry and, if present, decodes it, returning a `{ patternType }`
  // override the writer evaluates `readOnly` against INSTEAD of the
  // entity's current field — never a persisted write of `patternType`
  // through this path (only `doubleLine` is ever merged back; see
  // `buildTypedFieldPatch`'s `readOnlyFromChanges` handling for the
  // non-leak guarantee).
  //
  // @codex P2 round 4 — both companions decode through `decodePatternType-
  // Value` (label OR raw DXF integer "0"/"1"/"2"), NOT a bare `PATTERN_TYPE_
  // CODE[value]` lookup — a caller may legitimately submit the raw integer
  // (the SAME vocabulary `propertyTranslator.ts`'s own Hatch "Pattern type"
  // case already accepts); a bare label lookup would misclassify "0" as
  // unrecognised and either wrongly gate the shell or fall back to a stale
  // entity read for the document-level batch evaluation.
  make(
    "Double",
    "boolean",
    { path: "doubleLine", read: (e) => (e as HatchEntity).doubleLine },
    {
      constraints: {
        readOnly: (entity) => (entity as HatchEntity).patternType !== 0,
        // @codex P2 round 5 — scan the FULL raw `changes` batch FIRST (when
        // supplied), ORDER-INDEPENDENTLY, exactly like `readOnlyFromChanges`
        // below — NOT the bag param alone, which only reflects sibling
        // changes the shell's accumulator has ALREADY processed before
        // reaching "Double" (order-DEPENDENT: a batch listing "Double"
        // BEFORE "Pattern type" would otherwise decide against the STALE
        // pre-apply type here while `readOnlyFromChanges` — which always
        // scans the whole batch — decides against the batch's real final
        // intent on the document side, a shell↔document split by ordering
        // alone).
        //
        // @codex P2 round 7 — a batch "Pattern type" entry that IS present
        // but UNDECODABLE (malformed AI/CLI payload) must NOT be treated as
        // "no signal → conservative read-only": the real legacy translator
        // SILENTLY IGNORES a malformed Pattern-type submit (its own
        // `code !== undefined` guard), so Pattern type stays at whatever it
        // ALREADY WAS — exactly what `readOnlyFromChanges`'s defer resolves
        // to on the document side (falling through to `resolveReadOnly`
        // against the REAL, unchanged entity). The shell has no entity to
        // fall back to, but the bag's OWN CURRENT "Pattern type" cell (the
        // pre-batch snapshot) is the best available proxy for "what the
        // entity's field actually still is" — so an undecodable BATCH entry
        // falls through to the bag cell exactly like "no batch entry at
        // all", rather than a separate conservative-reject branch. Only a
        // genuinely UNRESOLVABLE signal (no batch entry AND no bag cell, OR
        // a bag cell that is ITSELF undecodable — e.g. corrupted/legacy
        // state with no batch member to fall back further) stays
        // conservatively read-only.
        readOnlyFromBag: (bag, changes) => {
          const patternTypeChange = changes?.find((c) => c.name === "Pattern type");
          if (patternTypeChange !== undefined) {
            const decodedFromChange = decodePatternTypeValue(patternTypeChange.value);
            if (decodedFromChange !== undefined) return decodedFromChange !== 0;
            // Malformed sibling submit — falls through to the bag cell below,
            // same as if "Pattern type" were absent from the batch entirely.
          }
          const patternTypeCell = bag["Pattern type"];
          // Missing key entirely -> no signal at all, defer to the writer.
          if (patternTypeCell === undefined) return undefined;
          // A PRESENT-but-undecodable BAG cell (no batch member to fall back
          // to further) stays CONSERVATIVELY read-only — never a false
          // accept on genuinely unknown/corrupted state.
          return decodePatternTypeValue(patternTypeCell) !== 0;
        },
        readOnlyFromChanges: (changes) => {
          const patternTypeChange = changes.find((c) => c.name === "Pattern type");
          if (patternTypeChange === undefined) return undefined;
          const decoded = decodePatternTypeValue(patternTypeChange.value);
          if (decoded === undefined) return undefined;
          return { patternType: decoded } as Partial<HatchEntity>;
        },
      },
      toRaw: (s) => ["yes", "true", "1"].includes(s.trim().toLowerCase()),
    },
  ),
  // Gradient is a STRUCTURAL HatchGradient object edited via the special editor
  // (HATCH-EDIT-DIALOG), NOT a plain text field — read-only classification so
  // S15 never patches a string into the object field (P2 R4 #6). Absent for
  // pattern/solid hatches. Not in the single-select bag (→ `__hatch_gradient`).
  make("Gradient", "text", { path: "gradient", read: (e) => (e as HatchEntity).gradient ?? null }, {
    channel: "read-only-calc",
    constraints: { readOnly: true },
    display: (e) => ((e as HatchEntity).gradient ? "Gradient" : ""),
  }),
];

export const viewportDescriptors: TypedPropertyDescriptor[] = [
  // Display lock is NOT a plain displayLocked set — it routes through
  // applyViewportSetLock (control-VP guard + VP-overrides XDATA + status-bit
  // lock flag in sync), so S15 must go via the viewport mutator (P2 R4 #7).
  make("Display lock", "boolean", { path: "displayLocked", read: (e) => (e as ViewportEntity).displayLocked }, {
    constraints: { sideEffects: "viewportMutator" },
    display: (e) => ((e as ViewportEntity).displayLocked ? "Yes" : "No"),
    // @codex P2 — accept the SAME truthy vocabulary the sibling extended-geometry
    // booleans use ({yes,true,1} — ArrayRect / Hatch `Associative`, Spline
    // `Rational`; "on" excluded for legacy parity). The palette emits "Yes"/"No",
    // but a command-line / AI `properties/apply` may submit "true"/"1"; without
    // this the toRaw returned `false` for those → the mutator (which reads
    // `sideEffect.raw === true`) would UNLOCK an already-locked viewport.
    toRaw: (s) => ["yes", "true", "1"].includes(s.trim().toLowerCase()),
  }),
  // "1:100"-style label — EDITABLE via the viewport scale dropdown, which
  // recomputes View height and persists through applyViewportSetScale (P2 R5 #4;
  // NOT read-only). read=derived ratio (height/viewHeight); display=label;
  // toRaw=parsed ratio; sideEffects routes S15 through the scale mutator.
  make("Scale", "text", { path: "", read: (e) => viewportScaleRatio(e as ViewportEntity) }, {
    constraints: { sideEffects: "viewportScaleMutator" },
    display: (e) => formatViewportScale(viewportScaleRatio(e as ViewportEntity)),
    toRaw: (s) => parseViewportScaleLabel(s),
  }),
  coord<ViewportEntity>("Center X", "center.x", (e) => e.center.x),
  coord<ViewportEntity>("Center Y", "center.y", (e) => e.center.y),
  num<ViewportEntity>("Width", "width", (e) => e.width, { exclusiveMin: 0 }),
  num<ViewportEntity>("Height", "height", (e) => e.height, { exclusiveMin: 0 }),
  // the MODEL-space view fields route through applyViewportSetView (view + scale
  // sync), NOT a plain field set — sideEffects flag so S15 uses the mutator
  // (P2 R4 #7 sweep — same class as Display lock).
  viewportView("View height", "viewHeight", (e) => e.viewHeight),
  viewportView("View center X", "viewCenter.x", (e) => e.viewCenter.x),
  viewportView("View center Y", "viewCenter.y", (e) => e.viewCenter.y),
  num<ViewportEntity>("View twist", "viewTwistAngle", (e) => e.viewTwistAngle),
  // deferred viewport-special MATCHPROP subsystem — no typed home yet.
  deferred("Snap", "boolean"),
  deferred("Grid", "boolean"),
  deferred("UCS icon", "boolean"),
];

export const wipeoutDescriptors: TypedPropertyDescriptor[] = [
  // Shown directly in the bag by dxfGeometry.buildWipeoutPreview but missed by
  // the capability roster (P2 #10). Frame renders On/Off (NOT Yes/No); Vertex
  // count is the read-only vertex tally. Per-vertex "Vertex N X/Y" rows are
  // dynamic (grip-backed indexed coords) — handled via the typed vertex array,
  // not per-index descriptors (named residual in the census).
  make(
    "Frame",
    "boolean",
    { path: "frame", read: (e) => (e as WipeoutEntity).frame },
    { display: (e) => ((e as WipeoutEntity).frame ? "On" : "Off"), toRaw: (s) => s.trim().toLowerCase() === "on" },
  ),
  calc<WipeoutEntity>("Vertex count", (e) => e.vertices.length),
];
